import { useState, useEffect, useCallback } from "react";
import {
  Layout,
  Page,
  Card,
  InlineGrid,
  Text,
  BlockStack,
  Tabs,
  Button,
  Spinner,
} from "@shopify/polaris";
import { useSelector, useDispatch } from "react-redux";
import { fetchCustomer } from "./customer-container.slice";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import CustomerDetails from "components/customer/customer-details/customer-details";
import CustomerQuickContact from "components/customer/customer-quick-contact/customer-quick-contact";
import useIsPhone from "features/files/use-is-phone";
import CustomerInvoices from "components/invoices/customer-invoices";
import Contacts from "components/contacts/Contacts";
import CustomerQuestionnaire from "components/customer-questionnaire/customer-questionnaire";
import "./style.scss";
import CustomerContainerSkeleton from "./customer-container-skeleton";
import SalesReturns from "components/sales-returns/SalesReturns";
import {
  setModalxComponent,
  setModalxComponentProps,
  setModalxOptions,
} from "components/modalx/modalx.slice";
import CustomerFinanceHistory from "containers/customer-finance-history/customer-finance-history";
import Addresses from "components/addresses/Addresses";
import Emails from "components/emails/Emails";
import CustomerOrders from "features/customers-orders/customer-orders";
import CustomerDocumentsSection from "./customer-documents-section";
import FinvaldaDetails from "./finvalda-details";
import useCustomerFiles, { DOCUMENTS_TAB_KEY } from "./useCustomerFiles";
import { getCountryName } from "constants/countries";
import { AuditLogPopover } from "components/audit-log/audit-log-popover";
import CustomerSettlements from "features/customer-settlements/customer-settlements";
import CustomerReservations from "features/customer-reservations/customer-reservations";
import CustomerProducts from "features/customer-products/customer-products";

// Order here is the ORDER the tabs are shown in — the content below is keyed
// off each entry's `key`, not its position, so reordering this array is safe.
const TABS_CONFIG = [
  {
    key: "orders",
    path: "/orders",
    i18nKey: "orders",
    getBadge: (customer) => customer?.orderCount || "0",
  },
  {
    key: "settlements",
    path: "/settlements",
    i18nKey: "settlements",
    getBadge: () => undefined,
  },
  {
    key: "reservations",
    path: "/reservations",
    i18nKey: "reservations",
    getBadge: () => undefined,
  },
  {
    key: "documents",
    path: "/documents",
    i18nKey: "documents",
    getBadge: (customer) => customer?.filesCount || "0",
  },
  {
    key: "products",
    path: "/products",
    i18nKey: "products",
    // The count depends on the period picked inside the tab, so no badge.
    getBadge: () => undefined,
  },
  {
    key: "invoices",
    path: "/invoices",
    i18nKey: "invoices",
    getBadge: (customer) => customer?.invoices?.length || "0",
  },
  // {
  //   key: "emailmessages",
  //   path: "/emailmessages",
  //   i18nKey: "email_messages",
  //   getBadge: (customer) => customer?.emailMessages?.length || "0",
  // },
  {
    key: "salesreturns",
    path: "/salesreturns",
    i18nKey: "sales_returns",
    getBadge: (customer) => customer?.salesReturns?.length || "0",
  },
  {
    key: "emails",
    path: "/emails",
    i18nKey: "emails",
    getBadge: (customer) => customer?.emails?.length || "0",
  },
];

const TAB_PATHS = TABS_CONFIG.map((tab) => tab.path);

function CustomerContainer() {
  const { customer, isLoading, errMsg, requestedCustomerId } = useSelector(
    (state) => state.customerReducer
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { customerId, companyId } = useParams();
  const location = useLocation();
  const isPhone = useIsPhone();

  const loadCustomerData = useCallback(() => {
    // isEdit: refetch in the background without nulling the cached customer, so
    // returning from a finance/sub-page renders the existing data immediately
    // instead of flashing the full-page loader.
    dispatch(fetchCustomer({ companyId, customerId, isEdit: true }));
  }, [companyId, customerId, dispatch]);

  useEffect(() => {
    loadCustomerData();
  }, [loadCustomerData]);

  useEffect(() => {
    const lastSegment = location.pathname.split("/").pop();
    const isTabPath = TAB_PATHS.includes(`/${lastSegment}`);
    if (!isTabPath) {
      navigate(`/app/${companyId}/customers/${customerId}/orders`, {
        replace: true,
      });
    }
  }, [location.pathname, customerId, companyId, navigate]);

  // Map the URL path to tab index
  const currentPath = location.pathname.split("/").pop();
  const initialSelectedTab =
    TAB_PATHS.indexOf(`/${currentPath}`) !== -1
      ? TAB_PATHS.indexOf(`/${currentPath}`)
      : 0;

  const [selected, setSelected] = useState(initialSelectedTab);
  // The Polaris `Tabs` component itself works off the numeric index above —
  // this is the tab's stable identity, used everywhere else so content
  // doesn't have to track position.
  const selectedTabKey = TABS_CONFIG[selected]?.key || TABS_CONFIG[0].key;
  const {
    searchQuery,
    handleSearchChange,
    selectedCategoryFilters,
    handleCategoryFilterChange,
    filteredFiles,
    isLoadingCustomerFiles,
    isLoadingFilteredFiles,
    handleOnFileChange,
  } = useCustomerFiles({
    companyId,
    customerId,
    selectedTab: selectedTabKey,
  });

  const handleTabChange = (selectedTabIndex) => {
    setSelected(selectedTabIndex);
    const newPath = TAB_PATHS[selectedTabIndex];
    navigate(`/app/${companyId}/customers/${customerId}${newPath}`, {
      replace: true,
    });
  };

  // Determine which files to display handled inside hook

  const isCustomerReady = customer && customer.id === customerId;

  // If loading the requested customer failed, show an error with a retry path
  // instead of an endless skeleton. requestedCustomerId scopes the error to the
  // customer that actually failed (the slice clears errMsg when a new fetch
  // starts), so a stale error never blocks a different customer.
  if (errMsg && requestedCustomerId === customerId && !isCustomerReady) {
    return (
      <Page
        fullWidth
        backAction={{
          content: "Customers",
          url: `/app/${companyId}/customers`,
        }}
        title={t("customer")}
      >
        <Card>
          <BlockStack gap="300">
            <Text as="p" tone="critical">
              {t("not_fetch", { defaultValue: "Could not load customer" })}
            </Text>
            <div>
              <Button onClick={loadCustomerData}>
                {t("retry", { defaultValue: "Retry" })}
              </Button>
            </div>
          </BlockStack>
        </Card>
      </Page>
    );
  }

  // Show the skeleton on a genuine first load, or while the cached customer
  // still belongs to a different customerId (just switched customers) — but
  // reuse the cached customer when returning to the same one, avoiding a flash.
  if (!isCustomerReady) {
    return (
      <CustomerContainerSkeleton
        selected={selectedTabKey}
        tabCount={TABS_CONFIG.length}
      />
    );
  }

  const tabs = TABS_CONFIG.map(({ i18nKey, getBadge }) => ({
    id: t(i18nKey),
    content: t(i18nKey),
    accessibilityLabel: t(i18nKey),
    panelID: t(i18nKey),
    badge: typeof getBadge === "function" ? getBadge(customer) : "0",
  }));

  const orderCreateHandler = () => {
    navigate(`/app/${companyId}/customers/${customerId}/orders/new`);
  };

  const OrderCreateNode = () => {
    return (
      <Button variant="primary" onClick={orderCreateHandler}>
        {t("create_order")}
      </Button>
    );
  };

  const handleCreateNewYear = () => {
    const currentYear = new Date().getFullYear();
    dispatch(setModalxComponent("CreateFinanceYearConfirm"));
    dispatch(
      setModalxComponentProps({
        companyId,
        customerId,
        year: currentYear,
      })
    );
  };

  // Build address from separate fields
  const buildAddress = () => {
    const addressParts = [];
    if (customer.street) addressParts.push(customer.street);
    if (customer.houseNumber) addressParts.push(customer.houseNumber);
    if (customer.city) addressParts.push(customer.city);
    if (customer.zipCodeLetters || customer.zipCodeFigures) {
      addressParts.push(
        `${customer.zipCodeLetters || ""}${customer.zipCodeFigures || ""}`
      );
    }
    if (customer.country) {
      addressParts.push(getCountryName(customer.country, t));
    }
    return addressParts.length > 0
      ? addressParts.join(", ")
      : customer.address || "";
  };

  const customerName = `${customer.name}${
    customer.legalName ? `, ${customer.legalName}` : ""
  }`;

  return (
    <Page
      fullWidth
      backAction={{ content: "Customers", url: `/app/${companyId}/customers` }}
      // A phone has room for neither a long title nor a full address: the VAT
      // code and the city go under the name, the full address is in the
      // contact block below.
      title={isPhone ? customerName : `${customerName} [${customer.vatCode}]`}
      subtitle={
        isPhone
          ? [customer.vatCode, customer.city].filter(Boolean).join(" · ")
          : buildAddress()
      }
      titleMetadata={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {isLoading && <Spinner size="small" />}
          <AuditLogPopover
            companyId={companyId}
            subjectType="Customer"
            subjectId={customerId}
            label={t("history", { defaultValue: "History" })}
          />
        </div>
      }
      primaryAction={OrderCreateNode()}
    >
      <CustomerQuickContact customer={customer} address={buildAddress()} />
      <Tabs tabs={tabs} selected={selected} onSelect={handleTabChange} />
      <Layout>
        <Layout.Section>
          <BlockStack gap="500">
            {selectedTabKey === "orders" && (
              <Card roundedAbove="sm">
                <CustomerOrders />
              </Card>
            )}
            {selectedTabKey === "settlements" && (
              <CustomerSettlements companyId={companyId} customerId={customerId} />
            )}
            {selectedTabKey === "reservations" && (
              <CustomerReservations companyId={companyId} customerId={customerId} />
            )}
            {selectedTabKey === DOCUMENTS_TAB_KEY && (
              <CustomerDocumentsSection
                t={t}
                companyId={companyId}
                customerId={customerId}
                searchQuery={searchQuery}
                handleSearchChange={handleSearchChange}
                selectedCategoryFilters={selectedCategoryFilters}
                handleCategoryFilterChange={handleCategoryFilterChange}
                isLoadingCustomerFiles={isLoadingCustomerFiles}
                isLoadingFilteredFiles={isLoadingFilteredFiles}
                filteredFiles={filteredFiles}
                handleOnFileChange={handleOnFileChange}
              />
            )}

            {selectedTabKey === "products" && (
              <Card roundedAbove="sm">
                <InlineGrid columns="1fr auto">
                  <Text as="h2" variant="headingSm">
                    {t("products")}
                  </Text>
                </InlineGrid>
                <CustomerProducts companyId={companyId} customerId={customerId} />
              </Card>
            )}
            {selectedTabKey === "invoices" && (
              <Card roundedAbove="sm">
                <InlineGrid columns="1fr auto">
                  <Text as="h2" variant="headingSm">
                    {t("invoices")}
                  </Text>
                </InlineGrid>
                <CustomerInvoices
                  invoices={customer.invoices}
                  customerId={customerId}
                  onCreate={() => {
                    // Pick one or more signed, uninvoiced orders; each selected
                    // order becomes its own invoice (draft, or built outright).
                    dispatch(
                      setModalxOptions({
                        title: t("create_invoice"),
                        size: "large",
                      })
                    );
                    dispatch(setModalxComponent("CreateInvoiceModal"));
                    dispatch(
                      setModalxComponentProps({
                        customerId,
                        onDone: () => {
                          dispatch(
                            fetchCustomer({
                              companyId,
                              customerId,
                              isEdit: true,
                            })
                          );
                        },
                      })
                    );
                  }}
                  onOpenInvoice={(invoice) => {
                    dispatch(
                      setModalxOptions({
                        title: t("create_invoice")
                      })
                    );
                    dispatch(setModalxComponent("InvoiceForm"));
                    dispatch(
                      setModalxComponentProps({
                        customerId,
                        orderId: invoice.order,
                        invoiceId: invoice.id,
                        onDone: () => {
                          dispatch(
                            fetchCustomer({
                              companyId,
                              customerId,
                              isEdit: true,
                            })
                          );
                        },
                      })
                    );
                  }}
                />
              </Card>
            )}
            {/* {selectedTabKey === "emailmessages" && (
              <Card roundedAbove="sm">
                <InlineGrid columns="1fr auto">
                  <Text as="h2" variant="headingSm">
                    {t("email_messages")}
                  </Text>
                </InlineGrid>
              </Card>
            )} */}
            {selectedTabKey === "salesreturns" && (
              <Card roundedAbove="sm">
                <InlineGrid columns="1fr auto">
                  <Text as="h2" variant="headingSm">
                    {t("sales_returns")}
                  </Text>
                </InlineGrid>
                <SalesReturns salesReturns={customer.salesReturns} />
              </Card>
            )}
            {selectedTabKey === "emails" && (
              <Card roundedAbove="sm">
                <InlineGrid columns="1fr auto">
                  <Text as="h2" variant="headingSm">
                    {t("emails")}
                  </Text>
                </InlineGrid>
                <Emails emails={customer.emails} />
              </Card>
            )}
          </BlockStack>
        </Layout.Section>
        <Layout.Section variant="oneThird">
          <BlockStack gap="500">
            {customer.isFinanceApprovalsEnabled !== false && (
              <CustomerFinanceHistory
                customer={customer}
                companyId={companyId}
                onCreateNewYear={handleCreateNewYear}
              />
            )}
            <CustomerDetails customer={customer} />
            <CustomerQuestionnaire
              customerId={customer.id}
              email={customer.email}
              forms={customer.forms}
            />
            <Contacts contacts={customer.contacts} customer={customer.id} />
            <Addresses
              addresses={customer.addresses}
              subjectId={customer.id}
              subject="customer"
            />

            <Card roundedAbove="sm">
              <InlineGrid columns="1fr auto">
                <Text as="h2" variant="headingSm">
                  {t("finvalda")}
                </Text>
              </InlineGrid>
              <FinvaldaDetails meta={customer.meta} t={t} />
            </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

export default CustomerContainer;
